import React, { useState, useEffect } from "react";
import { getApiUrl } from "../../api";
import { useAuthStore } from "../../store/authStore";
import { Link, useNavigate } from "react-router-dom";

export default function Login() {
  const [userId, setUserId] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [userIdMsg, setUserIdMsg] = useState<string>("");
  const [passwordMsg, setPasswordMsg] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [saveUserId, setSaveUserId] = useState<boolean>(false);
  const [approvalModalOpen, setApprovalModalOpen] = useState<boolean>(false);
  const { setAuth } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) return;

    const expiry = localStorage.getItem("token_expiry");
    if (expiry && Date.now() > Number(expiry)) {
      localStorage.removeItem("access_token");
      localStorage.removeItem("user_role");
      localStorage.removeItem("user_name");
      localStorage.removeItem("token_expiry");
      return;
    }
    (async () => {
      try {
        const res = await fetch(getApiUrl("/marubit/api/member/me"), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          navigate("/");
        } else {
          localStorage.removeItem("access_token");
          localStorage.removeItem("user_role");
          localStorage.removeItem("user_name");
          localStorage.removeItem("token_expiry");
        }
      } catch {
        localStorage.removeItem("access_token");
        localStorage.removeItem("user_role");
        localStorage.removeItem("user_name");
        localStorage.removeItem("token_expiry");
      }
    })();
  }, []);

  useEffect(() => {
    const savedUserId = localStorage.getItem("saved_user_id");
    const isSaved = localStorage.getItem("save_user_id") === "true";
    if (isSaved && savedUserId) {
      setUserId(savedUserId);
      setSaveUserId(true);
    }
  }, []);

  const handleSaveUserIdChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const checked = e.target.checked;
    setSaveUserId(checked);
    if (checked && userId.trim()) {
      localStorage.setItem("saved_user_id", userId);
      localStorage.setItem("save_user_id", "true");
    } else {
      localStorage.removeItem("saved_user_id");
      localStorage.removeItem("save_user_id");
    }
  };

  const handleUserIdChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    setUserId(value);
    if (saveUserId && value.trim()) {
      localStorage.setItem("saved_user_id", value);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setUserIdMsg("");
    setPasswordMsg("");

    let hasError = false;
    if (!userId) { setUserIdMsg("아이디를 입력해주세요."); hasError = true; }
    if (!password) { setPasswordMsg("비밀번호를 입력해주세요."); hasError = true; }
    if (hasError) return;

    setLoading(true);
    try {
      const res = await fetch(getApiUrl("/marubit/api/member/login"), {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ grant_type: "password", username: userId, password }),
      });

      const data = await res.json();

      if (res.ok) {
        const token: string = data.access_token;
        localStorage.setItem("access_token", token);

        const meRes = await fetch(getApiUrl("/marubit/api/member/me"), {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (meRes.ok) {
          const meData = await meRes.json();
          if (meData.is_active === 1) {
          localStorage.setItem("access_token", token);
          localStorage.setItem("user_role", String(meData.role ?? 0));
          localStorage.setItem("user_name", meData.name ?? "");
          localStorage.setItem("unique_member_no", meData.unique_member_no ?? "");
          localStorage.setItem("token_expiry", String(Date.now() + 60 * 60 * 1000));
          if (data.kiosk_code) localStorage.setItem("kiosk_code", data.kiosk_code);
          if (data.cms_access_token) localStorage.setItem("cms_access_token", data.cms_access_token);
          setAuth({
            role: meData.role ?? 0,
            name: meData.name ?? "",
            uniqueMemberNo: meData.unique_member_no ?? "",
            kioskCode: data.kiosk_code ?? "",
          });
            navigate("/");
          } else {
            localStorage.removeItem("access_token");
            setApprovalModalOpen(true);
          }
        }
      } else {
        if (res.status === 423) {
          // 비밀번호 5회 오류로 계정 잠김 → 비밀번호 찾기 페이지로 이동
          navigate("/auth/find_pw", {
            state: {
              locked: true,
              message: data.detail || "비밀번호를 5회 잘못 입력하여 계정이 잠겼습니다. 비밀번호를 재설정해주세요.",
            },
          });
        } else if (res.status === 401 || res.status === 400) {
          setPasswordMsg(data.detail || "아이디 또는 비밀번호가 올바르지 않습니다.");
        } else if (res.status === 403) {
          setApprovalModalOpen(true);
        } else {
          setPasswordMsg(data.detail || "로그인 중 오류가 발생했습니다.");
        }
      }
    } catch (err) {
      setPasswordMsg("서버 연결 오류: " + (err as Error).message);
    }
    setLoading(false);
  };

  // 인풋 공통 스타일 (focus/error 색상은 동적이라 인라인 유지)
  const inputStyle = (hasError: boolean): React.CSSProperties => ({
    borderColor: hasError ? "#ef4444" : "#e5e7eb",
  });

  return (
    <div className="min-h-[calc(100vh-200px)] flex items-center justify-center bg-[#f5f2f0]">
      <div className="w-full max-w-[420px] bg-white rounded-2xl shadow-[0_8px_32px_rgba(136,114,103,0.1)] px-9 py-10">

        <form onSubmit={handleSubmit} className="flex flex-col">

          {/* 아이디 */}
          <div className="mb-4">
            <label className="block text-[13px] font-semibold text-[#887267] mb-[6px]">
              아이디
            </label>
            <input
              type="text"
              value={userId}
              onChange={handleUserIdChange}
              autoComplete="username"
              placeholder="아이디를 입력하세요"
              className="w-full h-11 px-[14px] border-[1.5px] rounded-lg text-[14px] outline-none box-border transition-colors duration-150"
              style={inputStyle(!!userIdMsg)}
              onFocus={e => (e.currentTarget.style.borderColor = "#887267")}
              onBlur={e => (e.currentTarget.style.borderColor = userIdMsg ? "#ef4444" : "#e5e7eb")}
            />
            {userIdMsg && <span className="text-[12px] text-red-500 mt-1 block">{userIdMsg}</span>}
          </div>

          {/* 비밀번호 */}
          <div className="mb-2">
            <label className="block text-[13px] font-semibold text-[#887267] mb-[6px]">
              비밀번호
            </label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="current-password"
              placeholder="비밀번호를 입력하세요"
              className="w-full h-11 px-[14px] border-[1.5px] rounded-lg text-[14px] outline-none box-border transition-colors duration-150"
              style={inputStyle(!!passwordMsg)}
              onFocus={e => (e.currentTarget.style.borderColor = "#887267")}
              onBlur={e => (e.currentTarget.style.borderColor = passwordMsg ? "#ef4444" : "#e5e7eb")}
            />
            {passwordMsg && <span className="text-[12px] text-red-500 mt-1 block">{passwordMsg}</span>}
          </div>

          {/* 아이디 저장 */}
          <div className="flex items-center gap-2 my-3 mb-6">
            <input
              type="checkbox"
              id="saveUserId"
              checked={saveUserId}
              onChange={handleSaveUserIdChange}
              className="w-[15px] h-[15px] cursor-pointer accent-[#887267]"
            />
            <label htmlFor="saveUserId" className="text-[13px] text-[#555] cursor-pointer">
              아이디 저장
            </label>
          </div>

          {/* 로그인 버튼 */}
          <button
            type="submit"
            disabled={loading}
            className={[
              "w-full h-[46px] text-white border-none rounded-lg text-[15px] font-bold transition-colors duration-150",
              loading ? "bg-[#b8a39b] cursor-not-allowed" : "bg-[#887267] cursor-pointer hover:bg-[#6b5248]",
            ].join(" ")}
          >
            {loading ? "로그인 중..." : "로그인"}
          </button>

          {/* 하단 링크 */}
          <div className="flex justify-center items-center mt-5 gap-1">
            <Link to="/auth/find_id" className="px-3 text-[13px] text-[#666] no-underline">아이디 찾기</Link>
            <span className="text-gray-200">|</span>
            <Link to="/auth/find_pw" className="px-3 text-[13px] text-[#666] no-underline">비밀번호 찾기</Link>
            <span className="text-gray-200">|</span>
            <Link to="/auth/register" className="px-3 text-[13px] text-[#887267] font-semibold no-underline">회원가입</Link>
          </div>
        </form>
      </div>

      {/* 승인 모달 */}
      {approvalModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
          <div className="bg-white rounded-[14px] shadow-[0_8px_32px_rgba(0,0,0,0.15)] px-6 py-7 w-[300px] text-center">
            <p className="text-[14px] text-[#444] mb-5 leading-relaxed">
              승인되지 않은 계정입니다.<br />관리자 승인 후 로그인 가능합니다.
            </p>
            <button
              onClick={() => setApprovalModalOpen(false)}
              className="w-full h-[42px] bg-[#887267] text-white border-none rounded-lg text-[14px] font-bold cursor-pointer hover:bg-[#6b5248] transition-colors duration-150"
            >
              확인
            </button>
          </div>
        </div>
      )}
    </div>
  );
}